/**
 * 음성 녹음 관련 커스텀 훅
 * 
 * 기능:
 * - 버튼 클릭 시 즉시 녹음 시작 + 모달 표시
 * - 녹음 일시 중지/재개
 * - 현재까지 녹음된 음성 듣기
 * - 최대 3시간 녹음 지원
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import apiClient from '../../../../core/http/axios';
import { dispatchLearningRecordsInvalidate } from '../../../contexts/LearningRecordsCacheContext';

// 최대 녹음 시간: 3시간 = 10800초
const MAX_RECORDING_TIME = 10800;

/**
 * 시간을 HH:MM:SS 형식으로 포맷팅
 */
const formatRecordingTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * 기본 파일 제목 생성 (YYYYMMDD_P.{pageIndex}_)
 */
const generateDefaultFileName = (pageIndex?: string): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const dateStr = `${year}${month}${day}`;
  
  const pageStr = pageIndex ? `P.${pageIndex}` : 'P.0';
  
  return `${dateStr}_${pageStr}_`;
};

export interface UseRecordingReturn {
  // 상태
  isRecordingModalOpen: boolean;
  /** 마이크 에러 모달 (연결 안됨, 권한 거부 등) */
  micErrorModal: { isOpen: boolean; message: string } | null;
  /** 마이크 권한 요청 모달 (녹음 전 권한 안내) */
  isPermissionRequestModalOpen: boolean;
  openPermissionRequestModal: () => void;
  closePermissionRequestModal: () => void;
  /** 권한 모달에서 확인 시: 모달 닫고 실제 녹음 시작 (성공 시 true 반환) */
  confirmPermissionAndStartRecording: () => Promise<boolean>;
  isRecording: boolean;
  isPaused: boolean;
  recordingTime: number;
  isUploading: boolean;
  isPlaying: boolean;
  playbackTime: number;
  audioChunksRef: React.MutableRefObject<Blob[]>;
  fileName: string;
  setFileName: React.Dispatch<React.SetStateAction<string>>;
  baseFileName: string;
  
  // 기존 액션 (호환성 유지)
  openRecordingModal: () => void;
  closeRecordingModal: () => void;
  closeMicErrorModal: () => void;
  closeRecordingModalOnly: () => void; // 모달만 닫기 (상태 유지)
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  cancelRecording: () => void;
  uploadRecording: () => Promise<void | { jobUuid: string }>;
  resetRecording: () => void;
  
  // 새로운 액션
  startRecordingWithModal: () => Promise<boolean>;
  startRecordingSilent: () => Promise<boolean>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  playPreview: () => void;
  stopPreview: () => void;
  
  // 유틸
  formatTime: (seconds: number) => string;
  getProgressPercent: () => number;
  
  // 옵션
  options?: UseRecordingOptions;
}

export interface UseRecordingOptions {
  autoCreateLr?: boolean;
  showTitleInput?: boolean;
}

/** getUserMedia 에러를 사용자 메시지로 변환 */
const getMicErrorMessage = (error: unknown): string => {
  if (error instanceof DOMException) {
    switch (error.name) {
      case 'NotFoundError':
        return '마이크가 연결되어 있지 않습니다. 마이크를 연결한 후 다시 시도해주세요.';
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return '마이크 접근 권한이 필요합니다. 브라우저 설정에서 마이크 권한을 허용해주세요.';
      case 'NotReadableError':
        return '마이크가 다른 앱에서 사용 중일 수 있습니다. 다른 앱을 종료한 후 다시 시도해주세요.';
      case 'OverconstrainedError':
        return '연결된 마이크가 요구 사항을 충족하지 않습니다. 다른 마이크를 사용해주세요.';
      default:
        break;
    }
  }
  return '마이크에 접근할 수 없습니다. 마이크 연결 및 브라우저 권한을 확인해주세요.';
};

export const useRecording = (bookUid?: string, pageIndex?: string, options?: UseRecordingOptions): UseRecordingReturn => {
  // 상태
  const [isRecordingModalOpen, setIsRecordingModalOpen] = useState(false);
  const [micErrorModal, setMicErrorModal] = useState<{ isOpen: boolean; message: string } | null>(null);
  const [isPermissionRequestModalOpen, setIsPermissionRequestModalOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [fileName, setFileName] = useState('');
  const [baseFileName, setBaseFileName] = useState('');
  
  // Refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const playbackTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current = null;
      }
    };
  }, []);

  // 기존 함수 (호환성 유지)
  const openRecordingModal = useCallback(() => {
    setIsRecordingModalOpen(true);
  }, []);

  const closeRecordingModal = useCallback(() => {
    if (isRecording || isPaused) {
      const confirmed = window.confirm('녹음이 진행 중입니다. 취소하시겠습니까?');
      if (confirmed) {
        cancelRecordingInternal();
      }
    } else {
      setIsRecordingModalOpen(false);
    }
  }, [isRecording, isPaused]);

  // 모달만 닫기 (상태 유지)
  const closeRecordingModalOnly = useCallback(() => {
    setIsRecordingModalOpen(false);
  }, []);

  // 마이크 에러 모달 닫기
  const closeMicErrorModal = useCallback(() => {
    setMicErrorModal(null);
  }, []);

  // 마이크 권한 요청 모달 (녹음 전 안내)
  const openPermissionRequestModal = useCallback(() => {
    setIsPermissionRequestModalOpen(true);
  }, []);

  const closePermissionRequestModal = useCallback(() => {
    setIsPermissionRequestModalOpen(false);
  }, []);

  /**
   * 내부 취소 함수 (confirm 없이)
   */
  const cancelRecordingInternal = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setIsPaused(false);
    
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    audioChunksRef.current = [];
    setRecordingTime(0);
    setIsRecordingModalOpen(false);
    setPlaybackTime(0);
    
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  /**
   * 마이크 확인 후 녹음 시작 (내부 로직)
   */
  const startRecordingWithModal = useCallback(async (): Promise<boolean> => {
    try {
      // 1. mediaDevices API 지원 여부 확인
      if (!navigator.mediaDevices?.getUserMedia) {
        setMicErrorModal({
          isOpen: true,
          message: '이 브라우저는 마이크 녹음을 지원하지 않습니다. HTTPS 환경에서 접속해주세요.',
        });
        return false;
      }

      // 2. 마이크 권한 요청 및 스트림 획득
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // MediaRecorder 생성
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // 데이터 수집
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // 녹음 시작
      mediaRecorder.start(1000); // 1초마다 데이터 수집
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      setIsRecordingModalOpen(true);

      // 타이머 시작
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_TIME) {
            // 최대 시간 도달 시 자동 중지
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              mediaRecorderRef.current.stop();
              setIsRecording(false);
              if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
              }
            }
            return prev;
          }
          return prev + 1;
        });
      }, 1000);

      return true;
    } catch (error) {
      // console.error('마이크 접근 오류:', error);
      const message = getMicErrorMessage(error);
      setMicErrorModal({ isOpen: true, message });
      return false;
    }
  }, []);

  /**
   * 모달 없이 바로 녹음 시작 (마이크 체크 후 즉시 녹음)
   */
  const startRecordingSilent = useCallback(async (): Promise<boolean> => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setMicErrorModal({
          isOpen: true,
          message: '이 브라우저는 마이크 녹음을 지원하지 않습니다. HTTPS 환경에서 접속해주세요.',
        });
        return false;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_TIME) {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              mediaRecorderRef.current.stop();
              setIsRecording(false);
              if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
              }
            }
            return prev;
          }
          return prev + 1;
        });
      }, 1000);

      return true;
    } catch (error) {
      const message = getMicErrorMessage(error);
      setMicErrorModal({ isOpen: true, message });
      return false;
    }
  }, []);

  /** 권한 모달에서 확인 클릭 시: 모달 닫고 녹음 시작 */
  const confirmPermissionAndStartRecording = useCallback(async (): Promise<boolean> => {
    setIsPermissionRequestModalOpen(false);
    return await startRecordingWithModal();
  }, [startRecordingWithModal]);

  // 기존 startRecording (모달 없이 녹음만 시작 - 호환성 유지)
  const startRecording = useCallback(async () => {
    await startRecordingWithModal();
  }, [startRecordingWithModal]);

  /**
   * 녹음 일시 중지
   */
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      
      // 타이머 일시 중지
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  }, [isRecording, isPaused]);

  /**
   * 녹음 재개
   */
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      
      // 타이머 재시작
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_TIME) {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              mediaRecorderRef.current.stop();
              setIsRecording(false);
              if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
              }
            }
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    }
  }, [isRecording, isPaused]);

  /**
   * 녹음 중지 (완료)
   */
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && (isRecording || isPaused)) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);
      setIsPaused(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      
      // 스트림 정리
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      // 기본 제목 설정
      const defaultTitle = generateDefaultFileName(pageIndex);
      setBaseFileName(defaultTitle);
      // 사용자가 이미 입력한 값이 없으면 기본 제목만 표시
      if (!fileName) {
        setFileName(defaultTitle);
      }
    }
  }, [isRecording, isPaused, pageIndex, fileName]);

  /**
   * 녹음 취소
   */
  const cancelRecording = useCallback(() => {
    cancelRecordingInternal();
  }, [cancelRecordingInternal]);

  /**
   * 녹음 리셋
   */
  const resetRecording = useCallback(() => {
    audioChunksRef.current = [];
    setRecordingTime(0);
    setPlaybackTime(0);
    setFileName('');
    setBaseFileName('');
  }, []);

  /**
   * 현재까지 녹음된 음성 듣기
   */
  const playPreview = useCallback(() => {
    if (audioChunksRef.current.length === 0) {
      alert('녹음된 내용이 없습니다.');
      return;
    }

    // 녹음 중이면 잠시 일시 중지
    const wasRecording = isRecording && !isPaused;
    if (wasRecording) {
      pauseRecording();
    }

    // 오디오 Blob 생성
    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    const audioUrl = URL.createObjectURL(audioBlob);
    
    // 오디오 플레이어 생성
    const audio = new Audio(audioUrl);
    audioPlayerRef.current = audio;
    
    setPlaybackTime(0);
    setIsPlaying(true);

    // 재생 진행 타이머
    playbackTimerRef.current = setInterval(() => {
      if (audio.currentTime) {
        setPlaybackTime(Math.floor(audio.currentTime));
      }
    }, 100);

    audio.onended = () => {
      setIsPlaying(false);
      setPlaybackTime(0);
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
        playbackTimerRef.current = null;
      }
      URL.revokeObjectURL(audioUrl);
    };

    audio.play();
  }, [isRecording, isPaused, pauseRecording]);

  /**
   * 미리듣기 중지
   */
  const stopPreview = useCallback(() => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    setIsPlaying(false);
    setPlaybackTime(0);
  }, []);

  /**
   * 녹음 업로드
   */
  const uploadRecording = useCallback(async () => {
    if (audioChunksRef.current.length === 0) {
      alert('녹음된 내용이 없습니다.');
      return;
    }

    setIsUploading(true);

    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('upload', audioBlob, `recording_${Date.now()}.webm`);
      formData.append('purpose', 'TEXT_EXTRACT');
      formData.append('book_uuid', bookUid || '');
      formData.append('visibility', 'PRIVATE');
      
      // auto_create_lr 옵션 (기본값: true)
      const autoCreateLr = options?.autoCreateLr !== false;
      formData.append('auto_create_lr', autoCreateLr ? 'true' : 'false');
      
      // 파일 제목: 기본 제목 + 사용자 입력
      const defaultTitle = baseFileName || generateDefaultFileName(pageIndex);
      let finalTitle = fileName || defaultTitle;
      
      // 사용자 입력이 기본 제목을 포함하지 않으면 기본 제목 뒤에 추가
      if (fileName && !fileName.startsWith(defaultTitle)) {
        finalTitle = `${defaultTitle}${fileName}`;
      } else if (!fileName) {
        finalTitle = defaultTitle;
      }
      
      formData.append('file_title', finalTitle);

      // 프록시를 통해 API 호출 (baseURL: /api + /viewer/files/upload = /api/viewer/files/upload)
      const response = await apiClient.post('/viewer/files/upload', formData, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'multipart/form-data',
        },
      });

      // console.log('업로드 성공:', response.data);

      if (bookUid) dispatchLearningRecordsInvalidate(bookUid);
      
      // auto_create_lr이 false인 경우 job_uuid를 반환해야 함
      if (!autoCreateLr && response.data?.data?.job_uuid) {
        const jobUuid = response.data.data.job_uuid;
        setIsUploading(false);
        return { jobUuid };
      }
      
      // 초기화
      audioChunksRef.current = [];
      setRecordingTime(0);
      setIsRecordingModalOpen(false);
      setPlaybackTime(0);
      setFileName('');
      setBaseFileName('');
    } catch (error: unknown) {
      // console.error('업로드 오류:', error);
      alert('업로드 중 오류가 발생했습니다.');
      throw error;
    } finally {
      setIsUploading(false);
    }
  }, [bookUid, fileName, pageIndex, baseFileName, options]);

  /**
   * 진행률 퍼센트 계산
   */
  const getProgressPercent = useCallback(() => {
    return (recordingTime / MAX_RECORDING_TIME) * 100;
  }, [recordingTime]);

  return {
    // 상태
    isRecordingModalOpen,
    micErrorModal,
    isRecording,
    isPaused,
    recordingTime,
    isUploading,
    isPlaying,
    playbackTime,
    audioChunksRef,
    fileName,
    setFileName,
    baseFileName,
    
    // 기존 액션 (호환성 유지)
    openRecordingModal,
    closeRecordingModal,
    closeRecordingModalOnly,
    closeMicErrorModal,
    isPermissionRequestModalOpen,
    openPermissionRequestModal,
    closePermissionRequestModal,
    confirmPermissionAndStartRecording,
    startRecording,
    stopRecording,
    cancelRecording,
    uploadRecording,
    resetRecording,
    
    // 새로운 액션
    startRecordingWithModal,
    startRecordingSilent,
    pauseRecording,
    resumeRecording,
    playPreview,
    stopPreview,
    
    // 유틸
    formatTime: formatRecordingTime,
    getProgressPercent,
    
    // 옵션
    options,
  };
};

